import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import FormLabel from "../../../components/core/typography/FormLabel";
import TextArea from "../../../components/core/form-components/TextArea";
import UploadFile from "../../../assets/images/icon_image_upload.svg";
import Button from "../../../components/core/form-components/Button";
import Paragraph from "../../../components/core/typography/Paragraph";
import { useDispatch, useSelector } from "react-redux";
import { languageIdSelector } from "../../../redux/slices/website/languageSlice";
import { LanguageTranslation } from "../../../components/language-translation/LanguageTranslation";
import { IoClose } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { hideLoader, showLoader } from "../../../redux/slices/siteLoaderSlice";
import { pathRoute } from "../../../routes/PathRoute";
import { userSelector } from "../../../redux/slices/userSlice";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Video from "yet-another-react-lightbox/plugins/video";
import { reelValidationSchema } from "../../../validations/website/createPostValidationSchema";
import VideoPreview from "../createPost/VideoPreview";
import { ToastShow } from "../../../redux/slices/toastSlice";
import Swal from "sweetalert2";
import {
  creatReelService,
  getReelConfigService,
} from "../../../services/website/reelService";
import { VideoTrimmer } from "./VideoTrimmer";

interface FormValues {
  description?: string;
  video?: any;
}

interface Photo {
  file: File | Blob;
  photos: string;
}

function CreateReel() {
  const language = useSelector(languageIdSelector);
  const langId = language?.languageId ?? "fr";
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const webProfileData = useSelector(userSelector);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | undefined>(undefined);
  const formikRef = useRef<any>(null);
  const reelValidationSchemaLang = reelValidationSchema(langId);
  const [allImage, setAllImages] = useState<Photo[]>([]);
  const [defaultInitialValues, setDefaultInitialValues] = useState<FormValues>({
    description: "",
    video: "",
  });
  const [charCount, setCharCount] = useState(0);
  const [reelConfig, setReelConfig] = useState<{
    reel_size: number;
    reel_duration: number;
  } | null>(null);

  // Trim modal states from reference
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [tempVideo, setTempVideo] = useState<{
    file: File;
    url: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      allImage.forEach((img) => {
        if (img.photos.startsWith("blob:")) {
          URL.revokeObjectURL(img.photos);
        }
      });
    };
  }, [allImage]);

  useEffect(() => {
    const fetchReelConfig = async () => {
      try {
        const response = await getReelConfigService();
        if (response?.data?.label) {
          setReelConfig({
            reel_size: response?.data?.label?.videoMaxSizeMB || 15,
            reel_duration: response?.data?.label?.videoMaxDuration || 50,
          });
        }
      } catch (error) {
        console.error("Failed to fetch reel config:", error);
        setReelConfig({ reel_size: 15, reel_duration: 50 });
      }
    };
    fetchReelConfig();

    if (id) {
      // For reel edit, implement fetch if needed
      // fetchReelDetails();
    }
  }, [id]);

  useEffect(() => {
    setCharCount(defaultInitialValues.description?.length || 0);
  }, [defaultInitialValues.description]);

  useEffect(() => {
    if (formikRef.current?.values?.description) {
      setCharCount(formikRef.current.values.description.length);
    }
  }, []);

  const openLightbox = (videoUrl: string) => {
    setVideoUrl(videoUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setVideoUrl(undefined);
  };

  const createReel = async (values: FormValues) => {
    const formData = new FormData();

    formData.append("type", "4");
    formData.append("text", (values.description || "").trim());
    formData.append("reelVideo", allImage[0].file as File);
    console.log(allImage[0].file);

    dispatch(showLoader());
    try {
      const response = await creatReelService(formData); // Assuming createPostVideo handles reelVideo

      if (response?.data?.code === 200) {
        navigate(pathRoute.website.reels, { state: { showDelay: true } });
      }
    } catch (error: any) {
      const messageKey =
        error?.response?.data?.message_key ||
        error?.data?.message_key ||
        error?.message_key;

      const translatedMessage = LanguageTranslation({
        labelName: messageKey || "",
        languageCode: langId || "fr",
      });

      dispatch(
        ToastShow({
          message: translatedMessage || error?.data?.message,
          type: "error",
        })
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  const onSubmit = async (values: FormValues) => {
    const hasExistingVideo = id && values.video;
    const hasNewVideo = allImage.length > 0;

    if (!hasExistingVideo && !hasNewVideo) {
      Swal.fire({
        icon: "error",
        title:
          LanguageTranslation({
            labelName: "web_upload_required_title",
            languageCode: langId || "fr",
          }) || "Oops...",
        html:
          LanguageTranslation({
            labelName: "web_upload_required_message",
            languageCode: langId || "fr",
          }) || "Please upload a video for your reel.",
        background: "#fefefe",
        confirmButtonColor: "#3085d6",
        confirmButtonText:
          LanguageTranslation({
            labelName: "ok",
            languageCode: langId || "fr",
          }) || "OK",
        showClass: {
          popup: "animate__animated animate__fadeInDown",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutUp",
        },
      });
      return;
    }

    if (id) {
      // Implement editReel if needed
      // handleEdit(values);
    } else {
      createReel(values);
    }
  };

  const handleVideoUpload = (
    setFieldValue: any,
    selectedFiles: FileList | null
  ) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const files = Array.from(selectedFiles);
    if (files.length > 1) {
      Swal.fire({
        icon: "error",
        title:
          LanguageTranslation({
            labelName: "web_upload_required_title",
            languageCode: langId || "fr",
          }) || "Oops...",
        html: "You can only upload 1 video.",
        confirmButtonText:
          LanguageTranslation({
            labelName: "ok",
            languageCode: langId || "fr",
          }) || "OK",
        confirmButtonColor: "#3085d6",
        background: "#fefefe",
      });
      return;
    }

    const file = files[0];
    const maxSizeMB = reelConfig?.reel_size || 15;

    // Revoke previous blob if exists
    if (allImage.length > 0 && allImage[0].photos.startsWith("blob:")) {
      URL.revokeObjectURL(allImage[0].photos);
    }

    const allowedTypes = ["video/mp4", "video/quicktime"];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: "Invalid file format",
        html: "Invalid file format. Please upload a video in MP4 or MOV format.",
        background: "#fefefe",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        showClass: {
          popup: "animate__animated animate__fadeInDown",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutUp",
        },
      });
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        html: `Video size must be less than ${maxSizeMB} MB.`,
        background: "#fefefe",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        showClass: {
          popup: "animate__animated animate__fadeInDown",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutUp",
        },
      });
      return;
    }

    // Check video duration
    const videoUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = videoUrl;
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(videoUrl);
      const duration = video.duration;
      const maxDuration = reelConfig?.reel_duration || 50;
      if (duration <= maxDuration) {
        // No trimming needed
        setAllImages([{ file, photos: URL.createObjectURL(file) }]);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Video uploaded successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        // Show trimmer
        const maxDuration = reelConfig?.reel_duration || 50;
        setTempVideo({ file, url: URL.createObjectURL(file) });
        setShowTrimmer(true);
        Swal.fire({
          icon: "info",
          title: "Video Too Long",
          text: `Video is ${Math.round(
            duration
          )}s. Please trim to ${maxDuration}s or less.`,
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
          background: "#fefefe",
        });
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      Swal.fire({
        icon: "error",
        title: "Invalid video",
        html: "Could not read video metadata.",
        background: "#fefefe",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        showClass: {
          popup: "animate__animated animate__fadeInDown",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutUp",
        },
      });
    };
  };

  const handleTrimComplete = (trimmedFile: File) => {
    // Clean up temp video
    if (tempVideo?.url) {
      URL.revokeObjectURL(tempVideo.url);
    }
    // Set the trimmed video
    setAllImages([
      {
        file: trimmedFile,
        photos: URL.createObjectURL(trimmedFile),
      },
    ]);
    setShowTrimmer(false);
    setTempVideo(null);
    Swal.fire({
      icon: "success",
      title: "Success",
      text: "Video trimmed and ready!",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const handleTrimCancel = () => {
    // Clean up temp video
    if (tempVideo?.url) {
      URL.revokeObjectURL(tempVideo.url);
    }
    setShowTrimmer(false);
    setTempVideo(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = () => {
    if (allImage.length > 0 && allImage[0].photos.startsWith("blob:")) {
      URL.revokeObjectURL(allImage[0].photos);
    }
    setAllImages([]);
    const fileInput = document.getElementById(
      "upload-file"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <>
      <div className="xl:py-6 py-4 xl:px-10 lg:px-6 md:px-4 px-3 ">
        <Paragraph text24 className="md:mb-6 sm:mb-5 mb-4">
          {id
            ? LanguageTranslation({
                labelName: "edit_post_new",
                languageCode: langId || "fr",
              }) || "Edit Post"
            : LanguageTranslation({
                labelName: "create_new_reel",
                languageCode: langId || "fr",
              }) || "Create New Reel"}
        </Paragraph>
        <div className="border border-medium-grey rounded-2xl overflow-hidden">
          <Formik
            key={langId}
            initialValues={defaultInitialValues}
            onSubmit={onSubmit}
            enableReinitialize={!!id}
            validationSchema={reelValidationSchemaLang}
            innerRef={formikRef}
            validateOnChange={false}
          >
            {({
              isSubmitting,
              values,
              setFieldValue,
              touched,
              errors,
              setErrors,
              setFieldTouched,
            }) => (
              <Form>
                <div className="grid grid-cols-12">
                  <div className="xxl:col-span-3 sm:col-span-4 col-span-12 bg-light-grey xl:p-30 md:p-5 p-3">
                    <FormLabel>
                      {LanguageTranslation({
                        labelName: "upload_video",
                        languageCode: langId || "fr",
                      }) || "Upload video"}
                    </FormLabel>
                    <label
                      htmlFor="upload-file"
                      className="px-4 py-3.5 md:h-[180px] h-[120px] bg-white rounded-lg border border-dashed border-medium-grey flex flex-col justify-center items-center gap-3 cursor-pointer"
                    >
                      {!id && (
                        <input
                          ref={fileInputRef}
                          type="file"
                          name="video"
                          id="upload-file"
                          className="sr-only"
                          accept="video/mp4,video/quicktime"
                          onChange={(e) =>
                            handleVideoUpload(setFieldValue, e.target.files)
                          }
                        />
                      )}

                      {allImage.length > 0 ? (
                        <div className="block rounded-[20px] overflow-hidden relative w-full h-full">
                          <VideoPreview
                            src={allImage[0].photos}
                            className="h-full w-full object-cover rounded-[20px]"
                            onRemove={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleRemoveImage();
                            }}
                          />
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleRemoveImage();
                            }}
                            className="absolute top-2 right-2 w-6 h-6 bg-white text-black rounded-full flex items-center justify-center shadow-md z-10 cursor-pointer"
                          >
                            <IoClose size={16} />
                          </span>
                        </div>
                      ) : id && values.video ? (
                        <div className="block rounded-[20px] overflow-hidden relative w-full h-full">
                          <video
                            className="h-full w-full object-cover rounded-[20px]"
                            src={values.video}
                            controls
                          />
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setFieldValue("video", "");
                              setAllImages([]);
                            }}
                            className="absolute top-2 right-2 w-6 h-6 bg-white text-black rounded-full flex items-center justify-center shadow-md z-10 cursor-pointer"
                          >
                            <IoClose size={16} />
                          </span>
                        </div>
                      ) : (
                        <div>
                          <img
                            src={UploadFile}
                            alt="upload-file"
                            width={30}
                            height={30}
                            className="mx-auto"
                          />
                          <Paragraph
                            text12
                            className="!text-dark-grey mt-2 text-center"
                          >
                            {LanguageTranslation({
                              labelName: "upload_video",
                              languageCode: langId || "fr",
                            }) || "Upload video"}
                          </Paragraph>
                        </div>
                      )}
                    </label>
                  </div>
                  <div className="xxl:col-span-9 sm:col-span-8 col-span-12 xl:p-30 md:p-5 p-3">
                    <div className="grid grid-cols-12 lg:gap-6 md:gap-5 gap-3">
                      <div className="col-span-12">
                        <div className="flex justify-between items-center mb-2">
                          <FormLabel>
                            {LanguageTranslation({
                              labelName: "description",
                              languageCode: langId || "fr",
                            }) || "Description"}
                          </FormLabel>
                          <span
                            className={`text-sm ${
                              charCount > 500 ? "text-red-500" : "text-gray-500"
                            }`}
                          >
                            {charCount}/500
                          </span>
                        </div>
                        <div className="relative">
                          <textarea
                            name="description"
                            rows={5}
                            value={values.description || ""}
                            placeholder={
                              LanguageTranslation({
                                labelName: "what_do_you_want_to_share",
                                languageCode: langId || "fr",
                              }) || "What do you want to share?"
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              setFieldValue("description", value);
                              setCharCount(value.length);
                            }}
                            className="block rounded-lg border border-medium-grey px-4 py-[14px] text-base leading-5 font-normal w-full focus:outline-0 focus:border-dark-grey placeholder:text-site-grey placeholder:font-normal text-site-black"
                          />
                          {charCount > 500 && (
                            <div className="text-site-red text-sm font-medium mt-1">
                              Description must be 500 characters or less.
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-12 mt-1.5">
                        <div className="flex items-center xl:justify-end justify-start gap-4">
                          <Button
                            type="button"
                            borderButton
                            className="lg:w-auto w-full"
                            onClick={() => {
                              if (formikRef.current) {
                                formikRef.current.resetForm({
                                  values: defaultInitialValues,
                                });
                              }
                              handleRemoveImage();
                            }}
                          >
                            {LanguageTranslation({
                              labelName: "cancel",
                              languageCode: langId || "fr",
                            }) || "Cancel"}
                          </Button>
                          {id ? (
                            <Button
                              primary
                              type="submit"
                              className="lg:w-auto w-full"
                            >
                              {LanguageTranslation({
                                labelName: "edit",
                                languageCode: langId || "fr",
                              }) || "Edit"}
                            </Button>
                          ) : (
                            <Button
                              primary
                              type="submit"
                              className="lg:w-auto w-full"
                              disabled={
                                (!values?.description ||
                                  values?.description.trim() === "") &&
                                allImage?.length === 0
                              }
                            >
                              {LanguageTranslation({
                                labelName: "post",
                                languageCode: langId || "fr",
                              }) || "Post"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Video Trimmer Modal from Reference */}
      {showTrimmer && tempVideo && reelConfig && (
        <VideoTrimmer
          videoFile={tempVideo.file}
          videoUrl={tempVideo.url}
          maxDuration={reelConfig?.reel_duration}
          onTrimComplete={handleTrimComplete}
          onCancel={handleTrimCancel}
        />
      )}

      {lightboxOpen && (
        <div className={"lightbox-no-navigation"}>
          <Lightbox
            open={lightboxOpen}
            close={closeLightbox}
            slides={[
              {
                type: "video",
                sources: [{ src: videoUrl || "", type: "video/mp4" }],
              },
            ]}
            plugins={[Video]}
            carousel={{
              finite: true,
              padding: 0,
            }}
            video={{
              autoPlay: true,
              controls: true,
              playsInline: true,
              loop: false,
            }}
          />
        </div>
      )}
    </>
  );
}

export default CreateReel;
